import { Menu, MenuButton, MenuItems, MenuSeparator } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForceViewerMode, useSetForceViewerMode } from "reducers/viewerMode";
import { useProfile } from "../../hooks/useProfile";
import { AuthContext, logout } from "../../reducers/auth";
import { PrivacyContext } from "../../reducers/privacy";
import { AppContext } from "../../reducers/reactStore";
import DisplayIfUserType from "../utils/DisplayIfUserType/DisplayIfUserType";
import ProfileDropdownMenuItem from "./ProfileDropdownMenuItem";

async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);

  if (crypto.subtle.digest === undefined) {
    throw new Error("Crypto is not supported");
  }
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function ProfileDropdown(): React.ReactElement {
  const user = useProfile();
  const navigate = useNavigate();

  const { dispatch } = useContext(AppContext);
  const privacyContext = useContext(PrivacyContext);
  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const forceViewerMode = useForceViewerMode();
  const setForceViewerMode = useSetForceViewerMode();

  const location = useLocation();

  const handleLogout = async (): Promise<void> => {
    dispatch({ type: "CLEAR_REFERER" });
    privacyContext.dispatch({ type: "LOGOUT" });
    logout(authContext.dispatch).finally(() => {
      authContext.dispatch({ type: "LOGOUT" });
      // Wipe all cached server data (including the profile) so the next user
      // starts clean; an empty profile cache also closes the admin route gate.
      queryClient.clear();
      navigate("/auth/login");
    });
  };

  const switchViewerMode = () => {
    setForceViewerMode(!forceViewerMode);
  };

  const switchPrivacyMode = () => {
    if (privacyContext.state.privacyMode) {
      privacyContext.dispatch({ type: "TURN_PRIVACY_MODE_OFF" });
    } else {
      privacyContext.dispatch({ type: "TURN_PRIVACY_MODE_ON" });
    }
  };

  const [hash, setHash] = useState<string | undefined>(undefined);

  useEffect(() => {
    sha256(user?.email_address as string)
      .then((data) => {
        setHash(data);
      })
      .catch((e) => {
        console.error(e);
        setHash("");
      });
  }, [user]);

  return (
    <Menu as="div" className="relative">
      <MenuButton
        className="-m-1.5 flex items-center p-1.5"
        data-cy={"navbar-profile"}
      >
        <span className="sr-only">Open user menu</span>
        <img
          alt="..."
          src={`https://www.gravatar.com/avatar/${hash}?d=identicon`}
          className="h-8 w-8 rounded-full bg-gray-50"
        />
        <span className="hidden lg:flex lg:items-center">
          <span
            aria-hidden="true"
            className="ml-4 text-sm font-semibold leading-6 text-gray-900"
          >
            {user?.fullname}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className="ml-2 h-5 w-5 text-gray-400"
          />
        </span>
      </MenuButton>
      <MenuItems
        transition
        className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <ProfileDropdownMenuItem
          name={"Your profile"}
          href={`/admin/profile${location.hash}`}
        />
        <DisplayIfUserType
          userTypes={"superadmin"}
          ignoreViewerModeSwitch={true}
        >
          <ProfileDropdownMenuItem
            name={`Switch to ${forceViewerMode ? "Admin" : "Viewer"} Mode`}
            action={switchViewerMode}
          />
        </DisplayIfUserType>
        <ProfileDropdownMenuItem
          name={`Switch Privacy Mode ${privacyContext.state.privacyMode ? "off" : "on"}`}
          action={switchPrivacyMode}
        />
        <MenuSeparator
          className={
            "col-span-full mx-3.5 my-1 h-px border-0 bg-zinc-950/5 sm:mx-3 dark:bg-white/10"
          }
        />
        <ProfileDropdownMenuItem
          name={"Sign out"}
          action={handleLogout}
          data-cy={"navbar-logout"}
        />
      </MenuItems>
    </Menu>
  );
}

export default ProfileDropdown;
